/* Refactored for better UX, validation, and code quality */
/* eslint-disable */
import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { getData, postData, putData } from "utils/Gateway";
import * as url from "helper/UrlHelper";

const AddUpdateLabelPopup = ({
  moduleName,
  afterTagModalClose,
  tagId,
  setTagId,
  setShowAlert,
  setAlertMessage,
  setMessageType,
}) => {
  const token = localStorage.getItem("token");
  const { t } = useTranslation();

  const [labelName, setLabelName] = useState("");
  const [labelSlug, setLabelSlug] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState({});

  // --- Improved Validation ---
  const validateForm = () => {
    const newErrors = {};
    const slugRegex = /^[A-Z0-9-]+$/; // Allows uppercase letters, numbers, and hyphens

    if (!labelName.trim()) {
      newErrors.name = t("A name is required for the label.");
    }
    if (!labelSlug.trim()) {
      newErrors.slug = t("A slug is required for the label's URL.");
    } else if (!slugRegex.test(labelSlug)) {
      newErrors.slug = t(
        "The slug can only contain uppercase letters, numbers, and hyphens."
      );
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // --- Optimized Data Fetching with useCallback ---
  const getTagDetails = useCallback(async () => {
    try {
      const requestUrl = `${url.API_BASE_URL}${url.API_GET_TAG_DETAILS}/${tagId}?token=${token}`;
      const response = await getData(requestUrl);
      if (response.status) {
        setLabelName(response.data.title);
        setLabelSlug(response.data.slug);
      }
    } catch (error) {
      setAlertMessage(error.message);
      setMessageType("error");
      setShowAlert(true);
    }
  }, [tagId, token, setAlertMessage, setMessageType, setShowAlert]);

  useEffect(() => {
    if (tagId) {
      getTagDetails();
    }
  }, [tagId, getTagDetails]);

  // --- Refactored Form Submission Handler ---
  const handleFormSubmit = async (e) => {
    e.preventDefault(); // Prevent default form submission
    if (!validateForm()) {
      return;
    }

    setIsSaving(true);
    try {
      const tagData = {
        title: labelName,
        slug: labelSlug.toUpperCase(),
        isglobal: moduleName === "administration",
      };

      let response;
      if (tagId) {
        const requestUrl = `${url.API_BASE_URL}${url.API_UPDATE_TAG}/${tagId}?token=${token}`;
        response = await putData(requestUrl, tagData);
      } else {
        const requestUrl = `${url.API_BASE_URL}${url.API_CREATE_NEW_TAG}?token=${token}`;
        response = await postData(requestUrl, tagData);
      }

      if (response.status) {
        setMessageType("success");
        bootstrap.Offcanvas.getInstance(
          document.querySelector("#addUpdateLabelOffCanvas")
        ).hide();
        resetHandler();
        afterTagModalClose();
      } else {
        setMessageType("error");
      }
      setAlertMessage(response.message);
      setShowAlert(true);
    } catch (error) {
      setAlertMessage(error.message);
      setMessageType("error");
      setShowAlert(true);
    } finally {
      setIsSaving(false);
    }
  };

  const resetHandler = () => {
    setLabelName("");
    setLabelSlug("");
    setTagId(null);
    setErrors({});
  };

  // --- Simplified Input Change Handlers ---
  const handleNameChange = (e) => {
    const name = e.target.value;
    setLabelName(name);
    // Auto-generate a clean slug as the user types the name
    setLabelSlug(name.trim().toUpperCase().replace(/\s+/g, "-"));
    if (errors.name) setErrors((prev) => ({ ...prev, name: "" }));
    if (errors.slug) setErrors((prev) => ({ ...prev, slug: "" }));
  };

  const handleSlugChange = (e) => {
    setLabelSlug(e.target.value);
    if (errors.slug) setErrors((prev) => ({ ...prev, slug: "" }));
  };

  return (
    <div
      className="offcanvas lg offcanvas-end bg-white border-0 shadow-lg"
      tabIndex="-1"
      id="addUpdateLabelOffCanvas"
      aria-labelledby="addUpdateLabelOffCanvas"
    >
      {/* Header design similar to SaveFeedbackRulePopup */}
      <div className="offcanvas-header bg-primary text-white p-4 pb-3 border-bottom border-gray-300">
        <div className="d-flex align-items-center gap-3">
          <div className="icon-wrapper p-3 bg-white rounded-circle d-flex align-items-center justify-content-center">
            <span className="material-symbols-outlined text-primary fs-5 icon-md">
              label
            </span>{" "}
            {/* Using 'label' icon */}
          </div>
          <div>
            <h3
              className="offcanvas-title fw-bold text-white mb-0"
              id="offcanvasLabelDetailsLabel"
            >
              {tagId ? t("Update Label") : t("Add New Label")}
            </h3>
            <p className="text-white fs-sm mb-0">
              {tagId
                ? t("Manage existing label details")
                : t("Define a new label")}
            </p>
          </div>
        </div>
        <button
          type="button"
          className="btn-close p-2 bg-gray-200 rounded-circle shadow-sm border-0"
          data-bs-dismiss="offcanvas"
          aria-label="Close"
          onClick={resetHandler}
        ></button>
      </div>
      <div className="offcanvas-body p-4">
        {/* Professional Card Layout */}
        <div className="p-3 p-md-4 border border-gray-300 rounded-10 shadow-sm bg-light h-100">
          <form
            onSubmit={handleFormSubmit}
            className="d-flex flex-column h-100"
            noValidate // Prevent default browser validation
          >
            <div className="fields-container flex-fill">
              {/* Label Name */}
              <div className="form-group mb-4 p-3 border rounded-8 shadow-sm bg-white">
                <label
                  htmlFor="labelName"
                  className="d-block fs-sm fw-semibold mb-2 text-dark"
                >
                  {t("Label Name")} <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  id="labelName"
                  className={`form-control fs-sm shadow-none ${
                    errors.name ? "is-invalid" : "border border-gray-300"
                  }`}
                  placeholder={t("e.g., JavaScript, Marketing Strategy")}
                  value={labelName}
                  onChange={handleNameChange}
                  aria-describedby="labelNameFeedback"
                />
                {errors.name && (
                  <div id="labelNameFeedback" className="invalid-feedback">
                    {errors.name}
                  </div>
                )}
              </div>

              {/* Slug */}
              <div className="form-group mb-4 p-3 border rounded-8 shadow-sm bg-white">
                <label
                  htmlFor="slug"
                  className="d-block fs-sm fw-semibold mb-2 text-dark"
                >
                  {t("Slug")} <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  id="slug"
                  className={`form-control fs-sm shadow-none ${
                    errors.slug ? "is-invalid" : "border border-gray-300"
                  }`}
                  placeholder={t("e.g., JAVASCRIPT, MARKETING-STRATEGY")}
                  value={labelSlug}
                  onChange={handleSlugChange}
                  aria-describedby="slugFeedback"
                />
                {errors.slug ? (
                  <div id="slugFeedback" className="invalid-feedback">
                    {errors.slug}
                  </div>
                ) : (
                  <div className="form-text fs-xs mt-2">
                    {t(
                      "This is the URL-friendly version of the name. It should be unique."
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="action d-flex justify-content-end pt-3 border-top mt-auto">
              <button
                type="submit"
                className="btn btn-primary d-flex align-items-center gap-2"
                disabled={isSaving}
                style={{
                  cursor: isSaving ? "not-allowed" : "pointer",
                }}
              >
                {isSaving ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    {t("Saving...")}
                  </>
                ) : (
                  <>
                    <span className="material-symbols-outlined me-1">save</span>{" "}
                    {t("Save Label")}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
export default AddUpdateLabelPopup;
